/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation, untranslated } from "@i18n";
import { renderUserTag } from "@CubeComponents/html/userTagDom";
import React, { useRef, useState, useEffect, forwardRef, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { convertDate } from "@Services/luxonHelper";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import IconComponent from "@CubeComponents/icons";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { showToastNotification, type ToastNotificationPayload } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { formFlowServiceUrl } from "@Services/dataManager";
import axiosWrapper from "@AxiosWrapper";
import { getBackendUrl } from "@Utils";
import type { FormControllerResponse } from "@ServerTypes/forms/forms.schema";
import { formFlowController } from "@Actions/Forms";
import img403 from "@Assets/images/noAccess.svg";
import { formCategories } from "@Helpers";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import FormFlowLibraryPopup from "./formFlowLibraryPopup";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import { type GridRowBase } from "@CubeComponents/html/SlickGrid/types";
import _ from "lodash";
import { getUserSelection } from "./formsHelpers";
import Text from "@CubeComponents/html/Text/Text";
import { RootState } from "@Reducers/index";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getProjectSettingsPageUrl } from "@Features/projects";
import SelectSpaces from "@Components/WorkspaceList/SelectSpaces";

/** Row type for form flow data */
interface FormFlowRow extends GridRowBase {
  name?: string;
  selectedForm?: { _id?: string; name?: string };
  space?: { spaceName: string };
  createdBy?: { _id?: string; fullName?: string } | string;
  createdDate?: number;
}

const FormFlows = forwardRef(
  ({ projectId, setFormFlowId, showOnlyGrid = false, customQuery, setNextBtnState, customService }: any, ref: any) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [spaceId, setSpaceId] = useState<string>("");
    const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [openAddEditModal, setOpenAddEditModal] = useState(false);
    const [addFromLibrary, setAddFromLibrary] = useState({ state: false, step: 1 });
    const [userSelectionMode, setUserSelectionMode] = useState("currentUser");
    const [urlSearchParams] = useSearchParams();
    const formCategory = urlSearchParams.get("resourceType")?.toUpperCase();
    const { getProjectSettingObject } = useSelector((state: RootState) => state);
    const [toolBtnState, setToolBtnState] = useState({
      create: true,
      edit: false,
      delete: false,
      duplicate: false,
      addToLibrary: false,
      archive: false,
    });
    const [showArchiveModal, setShowArchiveModal] = useState(false);
    const [archivedToolBtnState, setArchivedToolBtnState] = useState({
      unarchive: false,
    });
    const [showIllustration, setShowIllustration] = useState(false);
    const [isLoadingComplete, setIsLoadingComplete] = useState(false);
    const [archivedShowIllustration, setArchivedShowIllustration] = useState(false);
    const [archivedIsLoadingComplete, setArchivedIsLoadingComplete] = useState(false);

    const initialFormValues = {
      name: "",
      selectedTemplate: null,
      formCategory: formCategory?.toUpperCase() || formCategories.inspection,
      formFlow: [],
      projectId,
    };
    const [formFlowData, setFormFlowData] = useState<any>(initialFormValues);
    const [formTemplates, setFormTemplates] = useState<any[]>([]);

    const fetchFormTemplates = useCallback(async () => {
      if (formFlowData.formCategory) {
        const response = await axiosWrapper.post<FormControllerResponse>(`${getBackendUrl()}/forms/formController`, {
          value: {
            resourceType: formFlowData.formCategory,
            isArchived: false,
            projectId,
            params: { resourceType: formFlowData.formCategory, isArchived: false, projectId },
          },
        });
        if (response.data?.result) {
          setFormTemplates(response.data.result);
        }
      }
    }, [formFlowData.formCategory, projectId]);
    useEffect(() => {
      if (openAddEditModal) {
        fetchFormTemplates();
      }
    }, [openAddEditModal, fetchFormTemplates]);

    const [gridQuery, setGridQuery] = useState(
      customQuery || {
        projectId,
        formCategory: formFlowData.formCategory,
        isArchived: false,
      },
    );

    const gridService = customService || formFlowServiceUrl;

    // Stable-reference query for the archived-flows grid (only archived flows for this project + category).
    const archivedGridQuery = useMemo(
      () => ({
        projectId,
        formCategory: formFlowData.formCategory,
        isArchived: true,
      }),
      [projectId, formFlowData.formCategory],
    );

    useEffect(() => {
      if (!customQuery) {
        setGridQuery((prev: any) => ({
          ...prev,
          formCategory: formFlowData.formCategory,
        }));
      }
    }, [formFlowData.formCategory]);

    useEffect(() => {
      if (customQuery) {
        setGridQuery(customQuery);
      }
    }, [customQuery]);

    const tempRef = useRef<any>(null);
    const dataGrid = ref || tempRef;
    const archivedDataGrid = useRef<any>(null);
    const dispatch = useDispatch();

    const handleSelectOptionClick = (args: any) => {
      if (args.item.id == "create") {
        setOpenAddEditModal(true);
      } else if (args.item.id == "createFromLibrary") {
        setAddFromLibrary((prev) => ({ ...prev, state: true }));
      }
    };

    const refreshGrid = (isArchivedGrid = false) => {
      if (isArchivedGrid) {
        archivedDataGrid.current?.refresh();
      } else {
        dataGrid.current?.refresh();
      }
    };

    const createTemplate = () => {
      return (
        <SelectDropDown
          items={[
            { id: "create", text: t("Create") },
            { id: "createFromLibrary", text: t("Create From Library") },
          ]}
          select={handleSelectOptionClick}
          cssClass="e-caret-hide border-none ps-8 bg-white p-0"
        >
          <ButtonComponent id="add" buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}>
            <span className="flex gap-1 items-center justify-center">
              <Text>{t("Create")}</Text>
              <IconComponent name="arrowDownSm" width="1rem" height="1rem" />
            </span>
          </ButtonComponent>
        </SelectDropDown>
      );
    };
    const toolbarOptions = [
      {
        name: t("Create"),
        id: "custom_add",
        prefixIcon: "add",
        template: createTemplate,
        disabled: !toolBtnState.create,
      },
      {
        name: t("Edit"),
        prefixIcon: "editProfile",
        id: "custom_edit",
        disabled: !toolBtnState.edit,
      },
      {
        name: t("Delete"),
        prefixIcon: "delete",
        id: "custom_delete",
        disabled: !toolBtnState.delete,
      },
      {
        name: t("Duplicate"),
        prefixIcon: "Duplicate",
        id: "custom_duplicate",
        disabled: !toolBtnState.duplicate,
      },
      {
        name: t("Add to Library"),
        prefixIcon: "add",
        id: "custom_addToLibrary",
        disabled: !toolBtnState.addToLibrary,
      },
      {
        name: t("Archive"),
        prefixIcon: "archive",
        id: "custom_archive",
        disabled: !toolBtnState.archive,
      },
    ];

    const archivedToolbarOptions = [
      {
        name: t("Unarchive"),
        prefixIcon: "unarchive",
        id: "custom_unarchive",
        disabled: !archivedToolBtnState.unarchive,
      },
    ];

    const handleDeleteFormFlow = async () => {
      const selectedData = dataGrid.current.slickGrid
        .getSelectedRows()
        .map((data: any) => dataGrid.current.slickGrid.getDataItem(data));
      const formFlowIds: string[] = selectedData.map((data: any) => data._id);

      const data = {
        action: "delete" as const,
        formFlowIds,
        projectId,
      };
      const res = await formFlowController(data);
      let toastContent: ToastNotificationPayload;
      if (res.data.status) {
        toastContent = {
          content: t("Selected Form Flows have been deleted successfully"),
          type: TOAST_TYPE_SUCCESS,
        };
        refreshGrid();
      } else {
        toastContent = {
          content: untranslated(res.data.errorMessage),
          type: TOAST_TYPE_ERROR,
        };
      }
      dispatch(showToastNotification(toastContent));
      handleDeletModalClose();
    };

    const handleDeletModalClose = () => {
      setShowDeleteConfirmationModal(false);
    };

    const handleAddEditModalCLose = () => {
      setOpenAddEditModal(false);
      setEditMode(false);
      setFormFlowData((prev: any) => ({
        ...initialFormValues,
        formCategory: prev.formCategory,
      }));
    };

    const toolbarClickHandler = async (params: any) => {
      if (!dataGrid.current) return;
      const totalRowsSelected = dataGrid.current?.slickGrid
        .getSelectedRows()
        .map((data: any) => dataGrid.current.slickGrid.getDataItem(data));
      const selectedData = totalRowsSelected[0];
      if (params === "custom_delete") {
        setShowDeleteConfirmationModal(true);
      }
      if (params === "custom_archive") {
        handleArchiveFormFlow(dataGrid, true);
      }
      if (params === "custom_edit") {
        setFormFlowData({
          name: selectedData.name,
          selectedTemplate: selectedData.selectedForm._id,
          formCategory: selectedData.formCategory,
          projectId,
        });
        setOpenAddEditModal(true);
        setEditMode(true);
      }
      if (params === "custom_duplicate") {
        const data = {
          action: "duplicate" as const,
          sourceFormFlowId: selectedData._id,
        };

        const res = await formFlowController(data);
        if (res.data.status) {
          dispatch(
            showToastNotification({
              content: t("Form flow duplicated successfully"),
              type: TOAST_TYPE_SUCCESS,
            }),
          );
        }
        refreshGrid();
      }
      if (params === "custom_addToLibrary") {
        const res = await formFlowController({
          action: "addToLibrary",
          formFlowIds: totalRowsSelected.map((data: any) => data._id),
        });
        let toastContent = {
          content: t("Selected Form Flows have been added to library successfully"),
          type: TOAST_TYPE_SUCCESS,
        };
        if (!res.data.status) {
          toastContent = {
            content: untranslated(res.data.errorMessage),
            type: TOAST_TYPE_ERROR,
          };
        }
        refreshGrid();
        dispatch(showToastNotification(toastContent));
      }
    };

    // Archives (isArchived=true) or unarchives (isArchived=false) the rows selected in the given grid.
    const handleArchiveFormFlow = async (gridRef: any, isArchived: boolean) => {
      if (!gridRef.current) return;
      const selectedData = gridRef.current.slickGrid
        .getSelectedRows()
        .map((data: any) => gridRef.current.slickGrid.getDataItem(data));
      const formFlowIds: string[] = selectedData.map((data: any) => data._id);
      if (!formFlowIds.length) return;

      const res = await formFlowController({
        action: "archive",
        formFlowIds,
        isArchived,
        projectId,
      });

      let toastContent: ToastNotificationPayload;
      if (res.data.status) {
        toastContent = {
          content: isArchived
            ? t("Selected Form Flows have been archived successfully")
            : t("Selected Form Flows have been unarchived successfully"),
          type: TOAST_TYPE_SUCCESS,
        };
        refreshGrid(!isArchived);
      } else {
        toastContent = {
          content: untranslated(res.data.errorMessage),
          type: TOAST_TYPE_ERROR,
        };
      }
      dispatch(showToastNotification(toastContent));
    };

    const archivedToolbarClickHandler = async (params: any) => {
      if (!archivedDataGrid.current) return;
      if (params === "custom_unarchive") {
        handleArchiveFormFlow(archivedDataGrid, false);
      }
    };

    const archivedSelectionEvent = (args: any) => {
      if (!archivedDataGrid.current) return;
      const totalRowsSelected = args.detail.args.rows.length;
      setArchivedToolBtnState({
        unarchive: totalRowsSelected >= 1,
      });
    };

    const selectionEvent = (args: any) => {
      if (!dataGrid?.current) return;
      const totalRowsSelected = args.detail.args.rows.length;
      if (setNextBtnState) {
        setNextBtnState(totalRowsSelected > 0);
      }
      if (showOnlyGrid) {
        return;
      }
      if (totalRowsSelected == 0) {
        setToolBtnState({
          create: true,
          delete: false,
          edit: false,
          duplicate: false,
          addToLibrary: false,
          archive: false,
        });
      }
      if (totalRowsSelected == 1) {
        setToolBtnState({
          create: true,
          delete: true,
          edit: true,
          duplicate: true,
          addToLibrary: true,
          archive: true,
        });
      }
      if (totalRowsSelected > 1) {
        setToolBtnState({
          create: true,
          delete: true,
          edit: false,
          duplicate: false,
          addToLibrary: true,
          archive: true,
        });
      }
    };

    const formatCreatedDate = (
      _row: number,
      _cell: number,
      _value: number | undefined,
      _columnDef: unknown,
      dataContext: FormFlowRow,
    ) => {
      return convertDate(Number(dataContext.createdDate), "dateForTooltip");
    };

    const createdByTemplate = (
      _row: number,
      _cell: number,
      _value: FormFlowRow["createdBy"],
      _columnDef: unknown,
      dataContext: FormFlowRow,
    ) => {
      const container = document.createElement("div");
      const _id = typeof dataContext.createdBy === "object" ? dataContext.createdBy?._id : dataContext.createdBy;
      renderUserTag(container, { id: _id });
      return container;
    };

    const handleChange = (event: { target: { name: string; value: any } }) => {
      if (event.target.name === "selectedTemplate") {
        const value = {
          _id: event.target.value._id,
          name: event.target.value.name,
          template: event.target.value.template,
          description: event.target.value.description,
        };
        setFormFlowData({
          ...formFlowData,
          [event.target.name]: value,
        });
      } else {
        setFormFlowData({
          ...formFlowData,
          [event.target.name]: event.target.value,
        });
      }
    };

    const saveFromFlow = async (data: any) => {
      const res = await formFlowController(data);
      if (res.data.status) {
        const content = editMode
          ? t("Form flow updated successfully")
          : t('New Form Flow "{{name}}" has been created successfully', { name: formFlowData.name });
        dispatch(
          showToastNotification({
            content,
            type: TOAST_TYPE_SUCCESS,
          }),
        );
        setFormFlowData((prev: any) => ({ ...initialFormValues, formCategory: prev.formCategory }));
        handleAddEditModalCLose();
        refreshGrid();
        if (setNextBtnState) {
          setNextBtnState(false);
        }
      }
    };
    const handleCreateFormFlowClick = async () => {
      const data: any = {
        data: { ...formFlowData, spaceId, userSelectionMode },
        action: editMode ? "update" : "save",
      };
      if (editMode) {
        const selectedIndex = dataGrid.current.slickGrid.getSelectedRows()[0];
        const selectedData = dataGrid.current.slickGrid.getDataItem(selectedIndex);
        data._id = selectedData._id;
      }
      await saveFromFlow(data);
    };

    const headerContent = () => (
      <div className=" w-full flex justify-between rounded-md">
        <Text className="">{editMode ? t("Edit Form Flow") : t("Create New Form Flow")}</Text>
        {!editMode && (
          <SelectSpaces
            disablePermissionCheck
            data-testid="spaceDropdownField"
            projectId={projectId}
            selectedSpaceCallback={(params: { _id: string }) => {
              setSpaceId(params._id);
            }}
          />
        )}
      </div>
    );

    const handleUserSelectionModeChange = (args: React.ChangeEvent<HTMLInputElement>) => {
      setUserSelectionMode(args.target.value);
    };
    const modelContent = () => (
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Text>{t("Form Flow Name")}</Text>
          <span>
            <TextFieldComponent
              name="name"
              type="text"
              placeholder={t("Add form flow name")}
              showClearButton
              onChange={handleChange}
              value={formFlowData.name}
            />
          </span>
        </div>
        <div className="flex flex-col gap-2">
          <Text>{t("Select Template")}</Text>
          <span>
            <SelectDropDown
              searchable
              value={formFlowData.selectedTemplate}
              placeholder={t("Select Form")}
              disabled={formFlowData.formCategory == "" || editMode}
              onChange={(e) => handleChange({ target: { name: "selectedTemplate", value: e } })}
              buttonType={BUTTON_TYPES.BORDER_BUTTON}
              fields={{
                text: "name",
                value: "_id",
              }}
              options={formTemplates}
              buttonWidth="100%"
              popperWidth={300}
            />
          </span>
        </div>
        {!editMode && getUserSelection("Assign users to level", userSelectionMode, handleUserSelectionModeChange)}
      </div>
    );
    const footerContent = () => (
      <div className="flex gap-4 items-center mt-5">
        <ButtonComponent
          buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
          disabled={
            editMode
              ? formFlowData?.name?.trim() == ""
              : formFlowData?.name?.trim() == "" ||
                formFlowData?.formCategory == "" ||
                !spaceId ||
                !formFlowData.selectedTemplate
          }
          onClick={handleCreateFormFlowClick}
        >
          <Text>{editMode ? t("Update") : t("Create")}</Text>
        </ButtonComponent>
        <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={handleAddEditModalCLose}>
          <Text>{t("Cancel")}</Text>
        </ButtonComponent>
      </div>
    );

    const recordClick = async (args: any) => {
      const rowIndex = args.detail.args.row;
      const clickedRow = dataGrid.current.slickGrid.getDataItem(rowIndex);
      if (args.detail.args.cell != 0 && setFormFlowId) {
        setFormFlowId(clickedRow._id);
      }
    };

    const columnDefinitions = useMemo(
      () => [
        {
          field: "name",
          id: "name",
          name: t("Name"),
          filterType: "Text",
        },
        {
          field: "selectedForm",
          id: "selectedForm",
          name: t("Form Template"),
          filterType: "Text",
          formatter: (
            _row: number,
            _cell: number,
            _value: FormFlowRow["selectedForm"],
            _columnDef: unknown,
            dataContext: FormFlowRow,
          ) => _.get(dataContext, "selectedForm.name"),
          _valueGetter: (row) => row.selectedForm?.name,
        },
        {
          field: "space.spaceName",
          id: "space",
          name: t("Space Name"),
          filterType: "Text",
          _valueGetter: (row) => row.space?.spaceName,
          formatter: (
            _row: number,
            _cell: number,
            _value: FormFlowRow["space"],
            _columnDef: unknown,
            dataContext: FormFlowRow,
          ) => _.get(dataContext, "space.spaceName"),
        },
        {
          field: "createdBy",
          id: "createdBy",
          name: t("Created by"),
          filterType: "Text",
          formatter: createdByTemplate,
          _valueGetter: (row) => (typeof row.createdBy === "object" ? row.createdBy?.fullName : row.createdBy),
        },
        {
          field: "createdDate",
          id: "createdDate",
          name: t("Created Date"),
          filterType: "Date",
          formatter: formatCreatedDate,
        },
      ],
      [],
    );

    // Signals grid-ready with the loaded row count so each grid can toggle its own empty-state
    // illustration. dataCount is SlickGrid's post-load total (finalDataset.length): 0 ⇔ the grid
    // is empty — including after a refresh triggered by delete/archive. We must NOT read
    // gridRef.dataView here: React hasn't synced the new dataset into the DataView yet when this
    // fires, so it still holds the pre-refresh rows and would wrongly suppress the illustration.
    const onDataLoaded = (
      setLoadingComplete: (v: boolean) => void,
      setIllustration: (v: boolean) => void,
      dataCount?: number,
    ) => {
      setIllustration((dataCount ?? 0) === 0);
      setLoadingComplete(true);
    };

    const getGridComponent = ({
      gridRef,
      query,
      onSelectionChange,
      toolbarClick,
      toolbar,
      onRowClick,
      setLoadingComplete,
      setIllustration,
    }: {
      gridRef: any;
      query: any;
      onSelectionChange: (args: any) => void;
      toolbarClick: (params: any) => void;
      toolbar: any;
      onRowClick?: (args: any) => void;
      setLoadingComplete: (v: boolean) => void;
      setIllustration: (v: boolean) => void;
    }) => (
      <SlickGrid1
        ref={gridRef}
        columnDefinitions={columnDefinitions as any}
        enableSortingOption
        onSelectedRowIdsChanged={onSelectionChange}
        remoteDataSource={gridService}
        infiniteScroll
        query={query}
        onClick={onRowClick}
        enableFilteringOption
        enableGroupingOption
        enableColumnChooser
        toolbarClickHandler={toolbarClick}
        toolbar={toolbar as any}
        setGridReady={(_ready, count) => onDataLoaded(setLoadingComplete, setIllustration, count)}
      />
    );

    const emptyIllustration = (show: boolean, title: string, actions?: React.ReactNode) =>
      show ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-6 bg-white">
          <span className="flex flex-col items-center">
            <IconComponent name="NoData" className="h-52 w-52" />
            <Text className="text-2xl text-text_gray">{title}</Text>
          </span>
          {actions}
        </div>
      ) : null;

    const closeArchiveModal = () => {
      setShowArchiveModal(false);
      setArchivedToolBtnState({ unarchive: false });
      // Refresh the active grid so any rows unarchived while the modal was open reappear.
      dataGrid.current?.refresh();
    };

    const archiveModal = () => (
      <ModalStateDialogComponent
        allowModalPadding={false}
        width="80%"
        height="90%"
        headerTitle={<Text className="text-2xl font-semibold">{t("Archived Form Flows")}</Text>}
        isVisible={showArchiveModal}
        onCancel={closeArchiveModal}
        modelContent={
          <div className="relative flex w-full h-full overflow-auto">
            <div
              className={`${archivedIsLoadingComplete && !archivedShowIllustration ? "" : "invisible"} h-full w-full overflow-auto`}
            >
              {getGridComponent({
                gridRef: archivedDataGrid,
                query: archivedGridQuery,
                onSelectionChange: archivedSelectionEvent,
                toolbarClick: archivedToolbarClickHandler,
                toolbar: archivedToolbarOptions,
                setLoadingComplete: setArchivedIsLoadingComplete,
                setIllustration: setArchivedShowIllustration,
              })}
            </div>
            {emptyIllustration(archivedIsLoadingComplete && archivedShowIllustration, t("No Archived Form Flows"))}
          </div>
        }
        footerContent={
          <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={closeArchiveModal}>
            <Text>{t("Close")}</Text>
          </ButtonComponent>
        }
      />
    );
    const addEditModal = () => (
      <ModalStateDialogComponent
        allowModalPadding={false}
        footerContent={footerContent()}
        width="50%"
        headerTitle={headerContent()}
        onCancel={handleAddEditModalCLose}
        isVisible={openAddEditModal}
        modelContent={modelContent()}
      />
    );

    const deletePopup = () => (
      <ModalStateDialogComponent
        allowModalPadding={false}
        footerContent={
          <div className=" flex gap-4">
            <ButtonComponent onClick={handleDeleteFormFlow} buttonType="gradientButton">
              <Text>{t("Confirm")}</Text>
            </ButtonComponent>
            <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={handleDeletModalClose}>
              <Text>{t("Cancel")}</Text>
            </ButtonComponent>
          </div>
        }
        headerTitle={
          <div className=" w-full flex justify-between rounded-md">
            <Text className="">{t("Delete selected form flow(s)")}</Text>
          </div>
        }
        isVisible={showDeleteConfirmationModal}
        onCancel={handleDeletModalClose}
        modelContent={
          <Text className="whitespace-normal">{t("Are you sure you want to delete the selected form flow(s) ?")}</Text>
        }
        width="30%"
      />
    );

    const noAccessPage = () => (
      <div className="flex w-full h-full overflow-auto flex-col justify-center items-center gap-1">
        <img src={img403} alt="noAccess" />
        <Text className="font-semibold text-2xl">{t("You do not have access to this page")}</Text>
      </div>
    );

    const handlebackClick = () => {
      navigate(getProjectSettingsPageUrl({ projectId, tab: "projectModules" }));
    };

    const header = () => {
      return (
        <div className="flex justify-between items-center ps-2 pt-4 pb-2 pe-4 w-full">
          <div className="flex gap-4 items-center">
            <IconComponent className="cursor-pointer" onClick={handlebackClick} name="BackwardArrow" />
            <div className="flex gap-1 flex-col">
              <Text className="text-2xl font-semibold">{t("Form Flows")}</Text>
              <Text>{t("List of all the Form Flows in this Project")}</Text>
            </div>
          </div>
          <div onClick={() => setShowArchiveModal(true)} className="flex gap-1 cursor-pointer items-center">
            <IconComponent className="h-4 w-4" name="archive" />
            <Text>{t("View Archived")}</Text>
          </div>
        </div>
      );
    };
    const getFormFlowContent = () => (
      <div className="flex w-full flex-col h-full overflow-auto">
        {!showOnlyGrid && header()}
        <div className="relative flex w-full h-full overflow-auto">
          <div className={`${isLoadingComplete && !showIllustration ? "" : "invisible"} h-full w-full overflow-auto`}>
            {getGridComponent({
              gridRef: dataGrid,
              query: gridQuery,
              onSelectionChange: selectionEvent,
              toolbarClick: toolbarClickHandler,
              toolbar: !showOnlyGrid ? toolbarOptions : null,
              onRowClick: recordClick,
              setLoadingComplete: setIsLoadingComplete,
              setIllustration: setShowIllustration,
            })}
          </div>
          {emptyIllustration(
            isLoadingComplete && showIllustration,
            t("No Form Flow has been added yet"),
            !showOnlyGrid ? (
              <div className="flex flex-row gap-4">
                <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={() => setOpenAddEditModal(true)}>
                  <span className="flex items-center">
                    <IconComponent className="h-4" name="add" />
                    <Text>{t("Create")}</Text>
                  </span>
                </ButtonComponent>
                <ButtonComponent
                  buttonType={BUTTON_TYPES.TEXT_BUTTON}
                  onClick={() => setAddFromLibrary((prev) => ({ ...prev, state: true }))}
                >
                  <span className="flex items-center">
                    <IconComponent className="h-4" name="add" />
                    <Text>{t("Create From Library")}</Text>
                  </span>
                </ButtonComponent>
              </div>
            ) : undefined,
          )}
        </div>
      </div>
    );

    const handleCancelibrary = () => {
      setAddFromLibrary(() => ({ state: false, step: 1 }));
      setUserSelectionMode("currentUser");
      if (setNextBtnState) {
        setNextBtnState(false);
      }
    };

    const getFlowsFromLibrary = () => {
      return (
        <FormFlowLibraryPopup
          handleCancelibrary={handleCancelibrary}
          projectId={projectId}
          refreshGrid={refreshGrid}
          addFromLibrary={addFromLibrary}
          setAddFromLibrary={setAddFromLibrary}
          resourceType={formFlowData.formCategory}
        />
      );
    };

    return (
      <>
        {_.get(getProjectSettingObject, "formFlow.add.enable", false) ? getFormFlowContent() : noAccessPage()}
        {openAddEditModal && addEditModal()}
        {showDeleteConfirmationModal && deletePopup()}
        {addFromLibrary.state && getFlowsFromLibrary()}
        {showArchiveModal && archiveModal()}
      </>
    );
  },
);
FormFlows.displayName = "FormFlows";
export default FormFlows;
